import { Checkbox, Chip, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { alpha, type SxProps, type Theme } from "@mui/material/styles";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import type { DocSummary } from "@xop/dashboard-shared";
import { useState } from "react";
import type { JSX } from "react";
import { Link as RouterLink } from "react-router-dom";

import { buildDocShareItems } from "../docShareItems.js";

/** Per-row solution-area pill. Always rendered (so the list view shows the
 * solution for every doc). Many-to-many since 2026-06-27: the pill summarises
 * the doc's tags ("Unified Communications", "Unified Communications +1", or
 * "Uncategorized"), with the full list in a hover title. For editors it opens a
 * CHECKBOX menu (the live managed pillar list) and commits the new id set on
 * close via a minimal `solution_area_ids` PATCH; viewers get a static pill. */
export function SolutionCell({
  doc,
  areas,
  editable,
  busy,
  onPick,
}: {
  doc: DocSummary;
  areas: ReadonlyArray<{ id: number; slug: string; name: string }>;
  editable: boolean;
  busy: boolean;
  /** The full new id set to tag the doc with (commit-on-close). */
  onPick: (ids: number[]) => void;
}): JSX.Element {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // Local working set while the menu is open — drives the checkbox state so the
  // UI is responsive and never races the refetch. Each toggle also commits
  // immediately (a minimal PATCH), so closing the menu needs no save step.
  const [draft, setDraft] = useState<Set<number> | null>(null);

  const tags = doc.solution_areas ?? [];
  const origIds = (): Set<number> => new Set(tags.map((t) => t.id));
  const current = draft ?? origIds();

  const summary =
    tags.length === 0
      ? "Uncategorized"
      : tags.length === 1
        ? tags[0]!.name
        : `${tags[0]!.name} +${tags.length - 1}`;
  const label = busy ? "Saving…" : summary;
  const fullTitle = tags.length > 0 ? tags.map((t) => t.name).join(", ") : undefined;
  const isSet = tags.length > 0;

  const baseSx: SxProps<Theme> = {
    display: { xs: "none", sm: "inline-flex" },
    height: 22,
    maxWidth: 220,
    borderRadius: 0.75,
    fontSize: 12,
    "& .MuiChip-label": { px: 1, overflow: "hidden", textOverflow: "ellipsis" },
  };
  const toneSx: SxProps<Theme> = isSet
    ? { bgcolor: (t) => alpha(t.palette.primary.main, 0.1), color: "primary.main" }
    : { bgcolor: "surfaceSoft", color: "text.secondary" };

  if (!editable) {
    return (
      <Chip
        label={label}
        size="small"
        {...(fullTitle ? { title: fullTitle } : {})}
        data-testid={`docs-row-solution-${doc.id}`}
        sx={[baseSx, toneSx]}
      />
    );
  }

  const open = (e: React.MouseEvent<HTMLElement>): void => {
    setDraft(origIds());
    setAnchor(e.currentTarget);
  };
  const close = (): void => {
    setAnchor(null);
    setDraft(null);
  };
  const toggle = (id: number): void => {
    const base = new Set(draft ?? origIds());
    if (base.has(id)) base.delete(id);
    else base.add(id);
    setDraft(base);
    onPick([...base]);
  };

  return (
    <>
      <Chip
        label={label}
        size="small"
        clickable
        onClick={open}
        onDelete={(e) => open(e as unknown as React.MouseEvent<HTMLElement>)}
        deleteIcon={<ArrowDropDownIcon />}
        {...(fullTitle ? { title: fullTitle } : {})}
        data-testid={`docs-row-solution-${doc.id}`}
        aria-label={`Change solution areas for ${doc.title}`}
        sx={[baseSx, toneSx, { cursor: "pointer" }]}
      />
      <Menu anchorEl={anchor} open={anchor != null} onClose={close}>
        {areas.length === 0 && (
          <MenuItem disabled>No solution areas — add them from Manage</MenuItem>
        )}
        {areas.map((a) => (
          <MenuItem key={a.id} onClick={() => toggle(a.id)} sx={{ gap: 0.5, py: 0.25 }}>
            <Checkbox
              checked={current.has(a.id)}
              size="small"
              tabIndex={-1}
              sx={{ p: 0.25 }}
              inputProps={{ "aria-label": a.name }}
            />
            {a.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/** The per-row ⋮ (kebab) action menu. Read + share actions (View / Download
 * PDF / Share via email / Share to chat / Copy link) are always present; the
 * modify actions (Edit / Publish-Unpublish / Delete) only when the user may
 * modify this doc. Collapsing them keeps the row to a single line and matches
 * the requested design. The share trio is the SAME `buildDocShareItems` the
 * reader's `<ShareMenu>` uses; "Share to chat" needs a dialog, so the page
 * passes `onShareToChat` (it owns the one lifted `<ShareToChatDialog>`). */
export function RowActions({
  doc,
  canModify,
  pdfBusy,
  onPdf,
  onPublish,
  onUnpublish,
  onDelete,
  onShareToChat,
  onMove,
}: {
  doc: DocSummary;
  canModify: boolean;
  pdfBusy: boolean;
  onPdf: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onDelete: () => void;
  onShareToChat: () => void;
  /** "Move to folder…" — absent where a document has no folder to move
   * between (a release page). */
  onMove?: () => void;
}): JSX.Element {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = (): void => setAnchor(null);
  const shareItems = buildDocShareItems({ doc, onShareToChat });
  return (
    <>
      <IconButton
        size="small"
        aria-label={`Actions for ${doc.title}`}
        aria-haspopup="menu"
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={anchor != null} onClose={close}>
        <MenuItem component={RouterLink} to={`/documentation/library/${doc.slug}`} onClick={close}>
          View
        </MenuItem>
        <MenuItem
          disabled={pdfBusy}
          onClick={() => {
            onPdf();
            close();
          }}
        >
          {pdfBusy ? "Downloading PDF…" : "Download PDF"}
        </MenuItem>
        <Divider />
        {shareItems.map((item) => (
          <MenuItem
            key={item.key}
            data-testid={item.testId}
            onClick={() => {
              close();
              item.onClick();
            }}
          >
            {item.icon ? <ListItemIcon>{item.icon}</ListItemIcon> : null}
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
        {canModify && <Divider />}
        {canModify && (
          <MenuItem component={RouterLink} to={`/documentation/manage/${doc.slug}/edit`} onClick={close}>
            Edit
          </MenuItem>
        )}
        {canModify && onMove && (
          <MenuItem
            data-testid={`doc-move-${doc.id}`}
            onClick={() => {
              close();
              onMove();
            }}
          >
            Move to folder…
          </MenuItem>
        )}
        {canModify &&
          (doc.status === "published" ? (
            <MenuItem
              onClick={() => {
                onUnpublish();
                close();
              }}
            >
              Unpublish
            </MenuItem>
          ) : (
            <MenuItem
              onClick={() => {
                onPublish();
                close();
              }}
            >
              Publish
            </MenuItem>
          ))}
        {canModify && (
          <MenuItem
            onClick={() => {
              onDelete();
              close();
            }}
            sx={{ color: "error.main" }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

/** Inline status / featured pill on each doc row. */
export function StatusChip({ tone, children }: { tone: "good" | "neutral" | "warn"; children: React.ReactNode }): JSX.Element {
  const toneSx: SxProps<Theme> =
    tone === "good"
      ? { bgcolor: (t) => alpha(t.palette.success.main, 0.15), color: "success.main" }
      : tone === "warn"
        ? { bgcolor: (t) => alpha(t.palette.warning.main, 0.15), color: "warning.main" }
        : { bgcolor: "surfaceSoft", color: "text.secondary" };
  return (
    <Chip
      label={children}
      size="small"
      sx={[{ height: 22, borderRadius: 0.75, fontSize: 12, "& .MuiChip-label": { px: 1 } }, toneSx]}
    />
  );
}
